import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '@/contexts/CartContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useStoreConfig } from '@/contexts/StoreConfigContext';
import Logo from '@/components/storefront/Logo';
import PaymentIcons from '@/components/storefront/PaymentIcons';
import { Helmet } from 'react-helmet-async';
import { Input } from '@/components/ui/input';
import { Lock, Loader2, ChevronDown, ChevronUp, ShieldCheck, Truck, RotateCcw, Tag, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const COUNTRIES = [
  {code:'US',name:'United States'},{code:'GB',name:'United Kingdom'},{code:'CA',name:'Canada'},{code:'AU',name:'Australia'},
  {code:'DE',name:'Germany'},{code:'FR',name:'France'},{code:'IT',name:'Italy'},{code:'ES',name:'Spain'},{code:'NL',name:'Netherlands'},
  {code:'BE',name:'Belgium'},{code:'AT',name:'Austria'},{code:'CH',name:'Switzerland'},{code:'SE',name:'Sweden'},{code:'NO',name:'Norway'},
  {code:'DK',name:'Denmark'},{code:'FI',name:'Finland'},{code:'IE',name:'Ireland'},{code:'PT',name:'Portugal'},{code:'PL',name:'Poland'},
  {code:'CZ',name:'Czech Republic'},{code:'JP',name:'Japan'},{code:'KR',name:'South Korea'},{code:'CN',name:'China'},{code:'IN',name:'India'},
  {code:'BR',name:'Brazil'},{code:'MX',name:'Mexico'},{code:'AR',name:'Argentina'},{code:'CL',name:'Chile'},{code:'CO',name:'Colombia'},
  {code:'NZ',name:'New Zealand'},{code:'SG',name:'Singapore'},{code:'HK',name:'Hong Kong'},{code:'MY',name:'Malaysia'},{code:'TH',name:'Thailand'},
  {code:'PH',name:'Philippines'},{code:'ID',name:'Indonesia'},{code:'AE',name:'United Arab Emirates'},{code:'SA',name:'Saudi Arabia'},
  {code:'ZA',name:'South Africa'},{code:'IL',name:'Israel'},{code:'TR',name:'Turkey'},{code:'RO',name:'Romania'},{code:'HU',name:'Hungary'},
  {code:'GR',name:'Greece'},{code:'HR',name:'Croatia'},{code:'BG',name:'Bulgaria'},
].sort((a, b) => a.name.localeCompare(b.name));

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];
const AU_STATES = ['ACT','NSW','NT','QLD','SA','TAS','VIC','WA'];
const STATE_LABELS = { US: 'State', CA: 'Province', AU: 'State', GB: 'County' };

function getStateOptions(country) {
  if (country === 'US') return US_STATES;
  if (country === 'CA') return CA_PROVINCES;
  if (country === 'AU') return AU_STATES;
  return null;
}

let stripePromiseCache = null;
function getStripePromise(key) {
  if (!stripePromiseCache) stripePromiseCache = loadStripe(key);
  return stripePromiseCache;
}

const appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#000000',
    colorBackground: '#FFFFFF',
    colorText: '#1A1A1A',
    colorDanger: '#E8545A',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    fontSizeBase: '14px',
    spacingUnit: '4px',
    borderRadius: '8px',
    colorTextPlaceholder: '#9CA3AF',
  },
  rules: {
    '.Tab': {
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      padding: '12px 16px',
      backgroundColor: '#FFFFFF',
      color: '#1A1A1A',
    },
    '.Tab:hover': {
      color: '#000000',
      borderColor: '#000000',
    },
    '.Tab--selected': {
      borderColor: '#000000',
      backgroundColor: '#F9FAFB',
      color: '#000000',
      boxShadow: '0 0 0 1px #000000',
    },
    '.TabIcon': { color: '#1A1A1A' },
    '.TabIcon--selected': { color: '#000000' },
    '.TabLabel': { color: '#1A1A1A' },
    '.TabLabel--selected': { color: '#000000' },
    '.Input': {
      border: '1px solid #D1D5DB',
      borderRadius: '8px',
      padding: '12px 14px',
      fontSize: '14px',
      color: '#1A1A1A',
      backgroundColor: '#FFFFFF',
    },
    '.Input:focus': {
      borderColor: '#000000',
      boxShadow: '0 0 0 1px #000000',
    },
    '.Input::placeholder': { color: '#9CA3AF' },
    '.Label': {
      color: '#374151',
      fontWeight: '500',
      fontSize: '13px',
    },
  },
};

const STATIC_STORE_MODE = process.env.REACT_APP_STATIC_STORE_MODE === 'true';

function StaticCheckoutForm({ form, setForm, items, subtotal, total, formatPrice, clearCart }) {
  const [submitted, setSubmitted] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const stateOptions = getStateOptions(form.country);
  const stateLabel = STATE_LABELS[form.country] || 'State / Region';
  const inputCls = "h-12 rounded-lg font-sans text-sm text-[#1A1A1A] border-[#D1D5DB] placeholder:text-[#9CA3AF] focus:ring-1 focus:ring-[#000] focus:border-[#000]";
  const selectCls = "h-12 rounded-lg font-sans text-sm text-[#1A1A1A] border border-[#D1D5DB] focus:ring-1 focus:ring-[#000] focus:border-[#000] bg-white px-3 w-full appearance-none";

  const submitOrderRequest = (event) => {
    event.preventDefault();
    const orderNumber = `FS-${Date.now().toString().slice(-6)}`;
    localStorage.setItem('florashop_last_order_request', JSON.stringify({
      order_number: orderNumber,
      customer: form,
      items,
      subtotal,
      total,
      created_at: new Date().toISOString(),
    }));
    clearCart();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ backgroundColor: '#fff' }}>
        <CheckCircle size={44} className="mb-4" style={{ color: '#16a34a' }} />
        <h1 className="font-serif text-3xl mb-3" style={{ color: '#1A1A1A' }}>Order request received</h1>
        <p className="font-sans text-sm max-w-md mb-6" style={{ color: '#6B7280' }}>
          Checkout payments are being connected. We saved your request locally and the Florashop team can follow up at {form.email}.
        </p>
        <Link to="/shop" className="inline-block px-8 py-3 font-sans text-sm font-bold uppercase tracking-wider rounded-lg" style={{ backgroundColor: '#000', color: '#fff' }}>Continue Shopping</Link>
      </div>
    );
  }

  return (
    <div data-testid="static-checkout-page" className="min-h-screen" style={{ backgroundColor: '#fff' }}>
      <Helmet><title>Checkout - Florashop</title></Helmet>
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        <div className="lg:col-span-7 px-5 md:px-10 lg:px-14 py-8">
          <div className="mb-5"><Logo variant="dark" /></div>
          <nav className="flex items-center gap-2 font-sans text-xs mb-7" style={{ color: '#6B7280' }}>
            <Link to="/cart" className="hover:text-[#000]" style={{ color: '#6B7280' }}>Cart</Link>
            <span>/</span>
            <span style={{ color: '#1A1A1A', fontWeight: 600 }}>Checkout</span>
          </nav>

          <div className="mb-6 p-4 rounded-lg border" style={{ backgroundColor: '#F9FAFB', borderColor: '#E5E7EB' }}>
            <p className="font-sans text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>Payment setup in progress</p>
            <p className="font-sans text-sm" style={{ color: '#6B7280' }}>
              Submit your order request now. Payment collection will be enabled once the Florashop API is connected.
            </p>
          </div>

          <form onSubmit={submitOrderRequest}>
            <div className="mb-6">
              <p className="font-sans text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#374151' }}>Contact Information</p>
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                <div className="sm:col-span-3">
                  <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Email *" className={inputCls} required />
                </div>
                <div className="sm:col-span-2">
                  <Input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="Phone" className={inputCls} />
                </div>
              </div>
            </div>

            <div className="mb-6">
              <p className="font-sans text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#374151' }}>Shipping Address</p>
              <div className="space-y-3">
                <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name *" className={inputCls} required />
                <Input value={form.line1} onChange={e => set('line1', e.target.value)} placeholder="Address *" className={inputCls} required />
                <Input value={form.line2} onChange={e => set('line2', e.target.value)} placeholder="Apartment, suite, etc." className={inputCls} />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="col-span-2 sm:col-span-2">
                    <Input value={form.city} onChange={e => set('city', e.target.value)} placeholder="City *" className={inputCls} required />
                  </div>
                  {stateOptions ? (
                    <select value={form.state} onChange={e => set('state', e.target.value)} className={selectCls}>
                      <option value="">{stateLabel}</option>
                      {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <Input value={form.state} onChange={e => set('state', e.target.value)} placeholder={stateLabel} className={inputCls} />
                  )}
                  <Input value={form.zip} onChange={e => set('zip', e.target.value)} placeholder="ZIP *" className={inputCls} required />
                </div>
                <select value={form.country} onChange={e => { set('country', e.target.value); set('state', ''); }} className={selectCls}>
                  {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <button type="submit" className="w-full h-14 font-sans text-[15px] font-bold uppercase tracking-wider rounded-lg" style={{ backgroundColor: '#000', color: '#fff' }}>
              Submit Order Request
            </button>
            <Link to="/cart" className="font-sans text-sm mt-5 inline-block hover:text-[#000]" style={{ color: '#6B7280' }}>&larr; Return to cart</Link>
          </form>
        </div>

        <div className="lg:col-span-5 border-l px-5 md:px-10 lg:px-8 py-8" style={{ backgroundColor: '#F9FAFB', borderColor: '#E5E7EB' }}>
          <h2 className="font-sans text-xs uppercase tracking-[0.15em] font-bold mb-4" style={{ color: '#000' }}>Order Summary</h2>
          <div className="space-y-4 mb-5">
            {items.map(item => (
              <div key={item.item_id} className="flex items-center gap-3">
                <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border" style={{ borderColor: '#E5E7EB', backgroundColor: '#fff' }}>
                  {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm truncate" style={{ color: '#1A1A1A' }}>{item.title}</p>
                  {item.variant_title && <p className="font-sans text-xs" style={{ color: '#6B7280' }}>{item.variant_title}</p>}
                </div>
                <span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{formatPrice(item.unit_price * item.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-4 space-y-2" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex justify-between"><span className="font-sans text-sm" style={{ color: '#6B7280' }}>Subtotal</span><span className="font-sans text-sm">{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between"><span className="font-sans text-sm" style={{ color: '#6B7280' }}>Shipping</span><span className="font-sans text-sm font-medium" style={{ color: '#16a34a' }}>FREE</span></div>
            <div className="flex justify-between pt-3 border-t" style={{ borderColor: '#E5E7EB' }}>
              <span className="font-sans text-base font-bold" style={{ color: '#1A1A1A' }}>Total</span>
              <span className="font-sans text-xl font-bold" style={{ color: '#1A1A1A' }}>{formatPrice(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckoutForm({ orderId, form, setForm, items, subtotal, total, bundleDiscount, couponDiscount, coupon, itemCount, formatPrice, applyCoupon, removeCoupon }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showAddr2, setShowAddr2] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [showExpress, setShowExpress] = useState(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const stateOptions = getStateOptions(form.country);
  const stateLabel = STATE_LABELS[form.country] || 'State / Region';

  const inputCls = "h-12 rounded-lg font-sans text-sm text-[#1A1A1A] border-[#D1D5DB] placeholder:text-[#9CA3AF] focus:ring-1 focus:ring-[#000] focus:border-[#000]";
  const selectCls = "h-12 rounded-lg font-sans text-sm text-[#1A1A1A] border border-[#D1D5DB] focus:ring-1 focus:ring-[#000] focus:border-[#000] bg-white px-3 w-full appearance-none";

  const saveCustomerInfo = useCallback(async () => {
    if (!orderId) return;
    try {
      await api.post(`/checkout/update-order/${orderId}`, {
        customer_email: form.email,
        customer_name: form.name,
        customer_phone: form.phone,
        shipping_address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, country: form.country, zip: form.zip },
      });
    } catch {}
  }, [orderId, form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!form.email.trim() || !form.name.trim()) { toast.error('Email and name are required'); return; }
    if (!form.line1.trim() || !form.city.trim() || !form.zip.trim()) { toast.error('Please fill in your shipping address'); return; }
    setSubmitting(true);
    setError('');
    await saveCustomerInfo();
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/order-confirmation?order_id=${orderId}`,
        receipt_email: form.email,
        shipping: {
          name: form.name,
          address: { line1: form.line1, line2: form.line2, city: form.city, state: form.state, postal_code: form.zip, country: form.country },
        },
      },
    });
    if (stripeError) {
      setError(stripeError.message);
      toast.error(stripeError.message);
    }
    setSubmitting(false);
  };

  const handleExpressConfirm = async () => {
    if (!stripe || !elements) return;
    await saveCustomerInfo();
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/order-confirmation?order_id=${orderId}`,
      },
    });
    if (stripeError) { setError(stripeError.message); toast.error(stripeError.message); }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    const result = await applyCoupon(couponCode.trim());
    if (result?.error) toast.error(result.error);
    else { toast.success('Coupon applied!'); setCouponCode(''); }
    setCouponLoading(false);
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen" style={{ backgroundColor: '#fff' }}>
      <Helmet><title>Checkout - Florashop</title></Helmet>
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
          {/* LEFT — Form (58%) */}
          <div className="lg:col-span-7 px-5 md:px-10 lg:px-14 py-8">
            <div className="mb-5"><Logo variant="dark" /></div>
            <nav className="flex items-center gap-2 font-sans text-xs mb-7" style={{ color: '#6B7280' }}>
              <Link to="/cart" className="hover:text-[#000]" style={{ color: '#6B7280' }}>Cart</Link>
              <span>/</span>
              <span style={{ color: '#1A1A1A', fontWeight: 600 }}>Checkout</span>
            </nav>

            {/* Express Checkout */}
            <div className="border rounded-lg p-4 mb-4" style={{ borderColor: '#E5E7EB', backgroundColor: '#fff' }}>
              <p className="font-sans text-xs font-semibold uppercase tracking-wider mb-3 text-center" style={{ color: '#6B7280' }}>Express Checkout</p>
              {showExpress !== false ? (
                <ExpressCheckoutElement
                  options={{ buttonHeight: 48, buttonType: { applePay: 'buy', googlePay: 'buy' }, layout: { maxColumns: 3, maxRows: 1 } }}
                  onConfirm={handleExpressConfirm}
                  onReady={({ availablePaymentMethods }) => {
                    if (!availablePaymentMethods || Object.keys(availablePaymentMethods).length === 0) setShowExpress(false);
                  }}
                />
              ) : (
                <p className="font-sans text-xs text-center py-2" style={{ color: '#9CA3AF' }}>Express options not available on this device</p>
              )}
            </div>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 border-t" style={{ borderColor: '#E5E7EB' }} />
              <span className="font-sans text-xs uppercase tracking-wider" style={{ color: '#9CA3AF' }}>or</span>
              <div className="flex-1 border-t" style={{ borderColor: '#E5E7EB' }} />
            </div>

            <form onSubmit={handleSubmit}>
              {/* Contact */}
              <div className="mb-6">
                <p className="font-sans text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#374151' }}>Contact Information</p>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                  <div className="sm:col-span-3">
                    <Input data-testid="checkout-email" type="email" name="email" autoComplete="email" value={form.email} onChange={e => set('email', e.target.value)}
                      placeholder="Email *" className={inputCls} required />
                  </div>
                  <div className="sm:col-span-2">
                    <Input data-testid="checkout-phone" type="tel" name="phone" autoComplete="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                      placeholder="Phone" className={inputCls} />
                  </div>
                </div>
              </div>

              {/* Shipping */}
              <div className="mb-6">
                <p className="font-sans text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#374151' }}>Shipping Address</p>
                <div className="space-y-3">
                  <Input data-testid="checkout-name" name="name" autoComplete="name" value={form.name} onChange={e => set('name', e.target.value)}
                    placeholder="Full name *" className={inputCls} required />
                  <Input data-testid="checkout-address" name="address-line1" autoComplete="address-line1" value={form.line1} onChange={e => set('line1', e.target.value)}
                    placeholder="Address *" className={inputCls} required />
                  {showAddr2 ? (
                    <Input name="address-line2" autoComplete="address-line2" value={form.line2} onChange={e => set('line2', e.target.value)}
                      placeholder="Apartment, suite, etc." className={inputCls} />
                  ) : (
                    <button type="button" onClick={() => setShowAddr2(true)} className="font-sans text-xs underline" style={{ color: '#6B7280' }}>
                      + Add apartment, suite, etc.
                    </button>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="col-span-2 sm:col-span-2">
                      <Input name="city" autoComplete="address-level2" value={form.city} onChange={e => set('city', e.target.value)}
                        placeholder="City *" className={inputCls} required />
                    </div>
                    <div className="relative">
                      {stateOptions ? (
                        <select data-testid="checkout-state" name="state" autoComplete="address-level1" value={form.state} onChange={e => set('state', e.target.value)}
                          className={selectCls}>
                          <option value="">{stateLabel}</option>
                          {stateOptions.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <Input name="state" autoComplete="address-level1" value={form.state} onChange={e => set('state', e.target.value)}
                          placeholder={stateLabel} className={inputCls} />
                      )}
                    </div>
                    <Input name="postal-code" autoComplete="postal-code" value={form.zip} onChange={e => set('zip', e.target.value)}
                      placeholder="ZIP *" className={inputCls} required />
                  </div>
                  <div className="relative">
                    <select data-testid="checkout-country" name="country" autoComplete="country" value={form.country} onChange={e => { set('country', e.target.value); set('state', ''); }}
                      className={selectCls}>
                      {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Payment Element */}
              <div className="mb-6 border rounded-lg p-4" style={{ borderColor: '#E5E7EB', backgroundColor: '#fff' }}>
                <p className="font-sans text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#6B7280' }}>Payment</p>
                <PaymentElement options={{
                  layout: 'tabs',
                  defaultValues: { billingDetails: { name: form.name, email: form.email } },
                }} />
              </div>

              {error && (
                <div className="mb-4 px-4 py-3 rounded-lg" style={{ backgroundColor: 'rgba(232,84,90,0.06)', border: '1px solid rgba(232,84,90,0.2)' }}>
                  <p className="font-sans text-sm" style={{ color: '#E8545A' }}>{error}</p>
                </div>
              )}

              <button data-testid="complete-order-btn" type="submit" disabled={submitting || !stripe}
                className="w-full h-14 font-sans text-[15px] font-bold uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
                style={{ backgroundColor: '#000', color: '#fff', opacity: submitting ? 0.7 : 1 }}>
                {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Complete Order'}
              </button>
              <p className="text-center font-sans text-xs mt-3 mb-3" style={{ color: '#9CA3AF' }}>
                <Lock size={11} className="inline mr-1" />Your payment is secure and encrypted
              </p>
              <div className="flex items-center justify-center gap-4 mb-6">
                <Link to="/shipping-policy" className="font-sans text-[11px] underline" style={{ color: '#9CA3AF' }}>Shipping Policy</Link>
                <Link to="/returns" className="font-sans text-[11px] underline" style={{ color: '#9CA3AF' }}>Returns & Refunds</Link>
              </div>
              <Link to="/cart" className="font-sans text-sm hover:text-[#000]" style={{ color: '#6B7280' }}>&larr; Return to cart</Link>
            </form>
          </div>

          {/* RIGHT — Order Summary (42%) */}
          <div className="lg:col-span-5 border-l px-5 md:px-10 lg:px-8 py-8" style={{ backgroundColor: '#F9FAFB', borderColor: '#E5E7EB' }}>
            {/* Mobile toggle */}
            <button className="lg:hidden flex items-center justify-between w-full mb-4 py-3 px-4 rounded-lg border" style={{ borderColor: '#E5E7EB', backgroundColor: '#fff' }}
              onClick={() => setShowSummary(!showSummary)}>
              <span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>
                {showSummary ? 'Hide' : 'Show'} order summary ({itemCount})
              </span>
              <div className="flex items-center gap-2">
                <span className="font-sans text-sm font-bold" style={{ color: '#1A1A1A' }}>{formatPrice(total)}</span>
                {showSummary ? <ChevronUp size={16} style={{ color: '#6B7280' }} /> : <ChevronDown size={16} style={{ color: '#6B7280' }} />}
              </div>
            </button>

            <div className={`${showSummary ? '' : 'hidden lg:block'}`}>
              {/* Cart items */}
              <div className="space-y-4 mb-5">
                {items.map(item => (
                  <div key={item.item_id} className="flex items-center gap-3">
                    <div className="relative w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden border" style={{ borderColor: '#E5E7EB', backgroundColor: '#fff' }}>
                      {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
                      <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center font-sans text-[10px] font-bold"
                        style={{ backgroundColor: '#6B7280', color: '#fff' }}>{item.quantity}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-sans text-sm truncate" style={{ color: '#1A1A1A' }}>{item.title}</p>
                      {item.variant_title && <p className="font-sans text-xs" style={{ color: '#6B7280' }}>{item.variant_title}</p>}
                    </div>
                    <span className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>{formatPrice(item.unit_price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="mb-4 border-t pt-4" style={{ borderColor: '#E5E7EB' }}>
                {!coupon ? (
                  showCoupon ? (
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
                        <Input value={couponCode} onChange={e => setCouponCode(e.target.value)}
                          placeholder="Discount code" className="pl-9 h-10 font-sans text-sm rounded-lg border-[#D1D5DB]"
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleApplyCoupon())} />
                      </div>
                      <button type="button" onClick={handleApplyCoupon} disabled={couponLoading}
                        className="px-4 h-10 font-sans text-sm font-medium rounded-lg border transition-colors hover:bg-[#F3F4F6]"
                        style={{ borderColor: '#D1D5DB', color: '#374151' }}>
                        {couponLoading ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setShowCoupon(true)} className="font-sans text-sm hover:text-[#000] transition-colors" style={{ color: '#6B7280' }}>
                      Have a discount code?
                    </button>
                  )
                ) : (
                  <div className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ backgroundColor: 'rgba(34,197,94,0.06)' }}>
                    <span className="font-sans text-sm font-medium" style={{ color: '#16a34a' }}>{coupon.code} applied</span>
                    <button type="button" onClick={removeCoupon} className="font-sans text-xs underline" style={{ color: '#6B7280' }}>Remove</button>
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="border-t pt-4 space-y-2" style={{ borderColor: '#E5E7EB' }}>
                <div className="flex justify-between">
                  <span className="font-sans text-sm" style={{ color: '#6B7280' }}>Subtotal</span>
                  <span className="font-sans text-sm" style={{ color: '#1A1A1A' }}>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-sm" style={{ color: '#6B7280' }}>Shipping</span>
                  <span className="font-sans text-sm font-medium" style={{ color: '#16a34a' }}>FREE</span>
                </div>
                {bundleDiscount > 0 && (
                  <div className="flex justify-between">
                    <span className="font-sans text-sm" style={{ color: '#16a34a' }}>Bundle Savings</span>
                    <span className="font-sans text-sm" style={{ color: '#16a34a' }}>-{formatPrice(bundleDiscount)}</span>
                  </div>
                )}
                {couponDiscount > 0 && (
                  <div className="flex justify-between">
                    <span className="font-sans text-sm" style={{ color: '#16a34a' }}>Discount</span>
                    <span className="font-sans text-sm" style={{ color: '#16a34a' }}>-{formatPrice(couponDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t" style={{ borderColor: '#E5E7EB' }}>
                  <span className="font-sans text-base font-bold" style={{ color: '#1A1A1A' }}>Total</span>
                  <span className="font-sans text-xl font-bold" style={{ color: '#1A1A1A' }}>{formatPrice(total)}</span>
                </div>
              </div>

              {/* Trust signals */}
              <div className="mt-6 pt-5 border-t space-y-3" style={{ borderColor: '#E5E7EB' }}>
                <div className="flex items-center gap-3">
                  <ShieldCheck size={16} style={{ color: '#374151' }} />
                  <span className="font-sans text-xs" style={{ color: '#374151' }}>Secure checkout — SSL encrypted</span>
                </div>
                <div className="flex items-center gap-3">
                  <Truck size={16} style={{ color: '#374151' }} />
                  <span className="font-sans text-xs" style={{ color: '#374151' }}>Free shipping on all orders</span>
                </div>
                <div className="flex items-center gap-3">
                  <RotateCcw size={16} style={{ color: '#374151' }} />
                  <span className="font-sans text-xs" style={{ color: '#374151' }}>30-day hassle-free returns</span>
                </div>
              </div>

              <div className="mt-5">
                <PaymentIcons size={32} className="justify-center" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotal, total, bundleDiscount, couponDiscount, coupon, sessionToken, itemCount, applyCoupon, removeCoupon, clearCart } = useCart();
  const { formatPrice } = useCurrency();
  const { config } = useStoreConfig();
  const [clientSecret, setClientSecret] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState('');
  const [form, setForm] = useState({ email: '', phone: '', name: '', line1: '', line2: '', city: '', state: '', country: config?.contact?.country || 'US', zip: '' });

  useEffect(() => {
    if (STATIC_STORE_MODE) { setLoading(false); return; }
    if (!sessionToken || !items?.length) { setLoading(false); return; }
    api.post('/checkout/create-payment-intent', { cart_session_token: sessionToken })
      .then(({ data }) => {
        if (data.already_completed) { navigate(data.redirect_url); return; }
        setClientSecret(data.client_secret);
        setOrderId(data.order_id);
        setPublishableKey(data.publishable_key);
        setLoading(false);
      })
      .catch(err => { setInitError(err.response?.data?.detail || 'Failed to initialize checkout'); setLoading(false); });
  }, [sessionToken, items?.length, navigate]);

  if (!items?.length) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#fff' }}>
        <p className="font-sans text-base mb-4" style={{ color: '#6B7280' }}>Your cart is empty</p>
        <Link to="/shop" className="font-sans text-sm underline" style={{ color: '#1A1A1A' }}>Continue Shopping</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#fff' }}>
        <Loader2 size={28} className="animate-spin mb-3" style={{ color: '#1A1A1A' }} />
        <p className="font-sans text-sm" style={{ color: '#6B7280' }}>Preparing checkout...</p>
      </div>
    );
  }

  if (STATIC_STORE_MODE) {
    return (
      <StaticCheckoutForm
        form={form} setForm={setForm} items={items} subtotal={subtotal}
        total={total} formatPrice={formatPrice} clearCart={clearCart}
      />
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: '#fff' }}>
        <p className="font-sans text-base mb-2" style={{ color: '#E8545A' }}>{initError}</p>
        <Link to="/cart" className="font-sans text-sm underline" style={{ color: '#1A1A1A' }}>Return to cart</Link>
      </div>
    );
  }

  if (!clientSecret || !publishableKey) return null;

  return (
    <Elements stripe={getStripePromise(publishableKey)} options={{ clientSecret, appearance }}>
      <CheckoutForm
        orderId={orderId} form={form} setForm={setForm}
        items={items} subtotal={subtotal} total={total}
        bundleDiscount={bundleDiscount} couponDiscount={couponDiscount}
        coupon={coupon} itemCount={itemCount} formatPrice={formatPrice}
        applyCoupon={applyCoupon} removeCoupon={removeCoupon}
      />
    </Elements>
  );
}
